import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Database, Cloud, Brain, Users, 
  ArrowRight, Clock, Shield, Rocket, Scale,
  Search, PenTool, Wrench, RefreshCw,
  Quote
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function HomePage() {
  const services = [
    {
      icon: Database,
      title: 'Data',
      description: 'Modern data stack, reliable pipelines and actionable analytics.',
      link: '/data-engineering',
      image: 'https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/fwhoo3fb_Data%20Engineering%20.jpeg'
    },
    {
      icon: Cloud,
      title: 'Cloud',
      description: 'Secure, scalable and cost-efficient cloud foundations, Azure-first.',
      link: '/cloud-engineering',
      image: 'https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/ssoiec65_Cloud%20Engineering.jpeg'
    },
    {
      icon: Brain,
      title: 'AI Services',
      description: 'Practical AI solutions on top of your data and workflows.',
      link: '/ai-services',
      image: 'https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/nw1rtlb3_AI%20Services.jpeg'
    },
    {
      icon: Users,
      title: 'Engineering Talent',
      description: 'Dedicated engineering squads to scale your data, cloud and AI delivery.',
      link: '/talent-pods',
      image: 'https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/tn0tewb2_Engineering%20Talent.png'
    }
  ];

  const outcomes = [
    {
      icon: Clock,
      title: 'Faster insight to decision',
      description: 'Reduce time from raw data to decision-ready dashboards.'
    },
    {
      icon: Shield,
      title: 'Cloud cost and risk under control',
      description: 'Consolidate, modernize and secure workloads in the cloud.'
    },
    {
      icon: Rocket,
      title: 'AI that actually ships',
      description: 'Move from AI pilots to deployed copilots and automated workflows.'
    },
    {
      icon: Scale,
      title: 'Elastic engineering capacity',
      description: 'Scale squads up or down without the hiring overhead.'
    }
  ];

  const industries = [
    'SaaS and digital products modernizing their data and cloud stack.',
    'Enterprises upgrading legacy BI into modern analytics and AI.',
    'Recruitment and HR tech using AI and analytics to transform hiring.',
    'Education and public sector initiatives needing robust data platforms.'
  ];

  const processSteps = [
    { icon: Search, title: 'Discover', description: 'We map your goals, current stack and constraints.' },
    { icon: PenTool, title: 'Design', description: 'We propose architecture, scope and the right combination of services.' },
    { icon: Wrench, title: 'Deliver', description: 'We build, integrate and iterate with your teams.' },
    { icon: RefreshCw, title: 'Operate & evolve', description: 'We help you run, optimize and extend your platform and pods.' }
  ];

  const testimonials = [
    {
      quote: "Better & improved portal. Good feedback from our customers. Works really well on mobiles too.",
      author: "Raphael Ray Ho",
      company: "Senior Manager, Koyo Singapore Bearing (Pte) Ltd"
    },
    {
      quote: "Cynergy helped us transform our data infrastructure in record time. Their expertise in modern data stack technologies was evident from day one.",
      author: "Head of Data",
      company: "Technology Company"
    },
    {
      quote: "The engineering pod model gave us the flexibility to scale our team without the usual hiring headaches. Highly recommended for growing companies.",
      author: "CTO",
      company: "SaaS Startup"
    }
  ];

  // Customer logos with actual images
  const customerLogos = [
    { name: 'Virtusa', logo: 'https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/nlz1n7f1_virtusa.jpeg' },
    { name: 'NSEIT', logo: 'https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/mezgk5tz_NSEIT.png' },
    { name: 'Aspire Systems', logo: 'https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/87q91lwo_aspire.png' },
    { name: 'Tandems.ai', logo: 'https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/exro0fh5_tandems.png' },
    { name: 'JTEKT', logo: 'https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/ekd58qaq_JTEKT.png' }
  ];

  return (
    <div data-testid="home-page">
      <Helmet>
        <title>Cynergy Services - Data, Cloud & AI Engineering Studio</title>
        <meta name="description" content="Cynergy is a boutique engineering studio that designs and delivers data platforms, cloud foundations, AI solutions and dedicated engineering pods." />
        <meta name="keywords" content="data engineering, cloud engineering, AI services, engineering talent, digital transformation" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-gradient-to-br from-primary/5 via-white to-accent/5" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/fwhoo3fb_Data%20Engineering%20.jpeg"
            alt="Data Engineering"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 to-white"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-3xl"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.h1 
              className="heading-1 text-foreground mb-6"
              variants={fadeInUp}
              data-testid="hero-headline"
            >
              We build modern data, cloud and AI foundations for your business.
            </motion.h1>
            <motion.p 
              className="body-lg mb-8 max-w-2xl"
              variants={fadeInUp}
              data-testid="hero-subheadline"
            >
              Cynergy is a boutique engineering studio that designs and delivers data platforms, 
              cloud foundations, AI solutions and dedicated engineering pods.
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              variants={fadeInUp}
            >
              <Link to="/contact">
                <Button size="lg" className="btn-primary w-full sm:w-auto" data-testid="hero-cta-primary">
                  Book a 30-minute architecture review
                </Button>
              </Link>
              <Link to="/talent-pods">
                <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="hero-cta-secondary">
                  Explore our services
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
            <motion.p 
              className="text-sm text-muted-foreground mt-6"
              variants={fadeInUp}
            >
              Based in India & Singapore, delivering for APAC and global clients.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Customer Logos Banner */}
      <section className="py-8 bg-white border-y border-border/50" data-testid="customer-logos-section">
        <div className="container-custom">
          <p className="text-center text-sm text-muted-foreground mb-6">
            Trusted by leading organizations
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {customerLogos.map((customer, index) => (
              <div 
                key={index} 
                className="grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-300"
                data-testid={`customer-logo-${index}`}
              >
                <img 
                  src={customer.logo} 
                  alt={customer.name}
                  className="h-10 md:h-12 w-auto object-contain"
                  style={{ maxWidth: '120px' }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">
              Our Offerings
            </p>
            <h2 className="heading-2 text-foreground mb-4">
              Four focused offerings
            </h2>
            <p className="body-lg mb-12 max-w-2xl">
              Engineered to work together or on their own.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={service.link}>
                  <Card className="h-full card-hover border border-border/60 overflow-hidden group" data-testid={`service-card-${index}`}>
                    <CardContent className="p-0">
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                            <service.icon className="w-5 h-5 text-primary" />
                          </div>
                          <h3 className="heading-3 text-foreground">{service.title}</h3>
                        </div>
                        <p className="text-muted-foreground mb-4">{service.description}</p>
                        <span className="inline-flex items-center text-primary font-medium text-sm group-hover:gap-2 transition-all duration-200">
                          Learn more
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="section-padding bg-secondary/50" data-testid="outcomes-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">
              Value Delivered
            </p>
            <h2 className="heading-2 text-foreground">
              Outcomes we deliver
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {outcomes.map((outcome, index) => (
              <motion.div
                key={outcome.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border-border/60" data-testid={`outcome-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                      <outcome.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {outcome.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{outcome.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section-padding bg-white" data-testid="industries-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">
                Industries
              </p>
              <h2 className="heading-2 text-foreground mb-4">
                Where we help
              </h2>
              <p className="body-lg mb-8">
                We work with technology-driven organizations across industries.
              </p>
              <ul className="space-y-4">
                {industries.map((industry, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`industry-item-${index}`}>
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                    <span className="text-muted-foreground">{industry}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/jipc0tyd_About%20Us.jpeg"
                alt="Professional team"
                className="w-full rounded-sm shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-foreground text-white" data-testid="process-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">
              How We Work
            </p>
            <h2 className="heading-2">
              A simple, engineered engagement model
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
                data-testid={`process-step-${index}`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-4">
                    <step.icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-sm font-medium text-primary mb-2">Step {index + 1}</span>
                  <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {step.title}
                  </h3>
                  <p className="text-white/70 text-sm">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-primary/30"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-white" data-testid="testimonials-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-4">
              Testimonials
            </p>
            <h2 className="heading-2 text-foreground">
              Trusted by teams who need to move fast
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-secondary/30 border-0" data-testid={`testimonial-${index}`}>
                  <CardContent className="p-6">
                    <Quote className="w-8 h-8 text-primary/30 mb-4" />
                    <p className="text-foreground mb-6 italic">"{testimonial.quote}"</p>
                    <div>
                      <p className="font-semibold text-foreground">{testimonial.author}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.company}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding bg-primary" data-testid="final-cta-section">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-2 text-white mb-4">
              Have a data, cloud or AI challenge?
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Let's discuss how we can help you achieve your goals.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="secondary" 
                className="bg-white text-primary hover:bg-white/90"
                data-testid="final-cta-button"
              >
                Tell us about your challenge
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
